import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, 
  Plus, Edit2, Trash2, Sparkles, Check, X, Coffee,
  Sun, Utensils, Cookie, Soup, RefreshCw, Save, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API = process.env.REACT_APP_BACKEND_URL;

// Days of week in Spanish
const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DAYS_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// Meal types
const MEAL_TYPES = [
  { id: 'desayuno', name: 'Desayuno', icon: Coffee, color: 'amber' },
  { id: 'comida', name: 'Comida', icon: Utensils, color: 'emerald' },
  { id: 'snack', name: 'Snack', icon: Cookie, color: 'purple' },
];

const MenuCalendar = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [menuData, setMenuData] = useState({});
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [showProductPicker, setShowProductPicker] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'master_admin' || user?.role === 'produccion';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes, menuRes] = await Promise.all([
        fetch(`${API}/api/products`),
        fetch(`${API}/api/categories`),
        fetch(`${API}/api/menu-calendar?month=${currentDate.getMonth() + 1}&year=${currentDate.getFullYear()}`)
      ]);

      if (productsRes.ok) setProducts(await productsRes.json());
      if (categoriesRes.ok) setCategories(await categoriesRes.json());
      if (menuRes.ok) {
        const data = await menuRes.json();
        setMenuData(data.menu || {});
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getWeekDays = () => {
    const days = [];
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day + (day === 0 ? -6 : 1)); // Start from Monday

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const getMenuForDay = (date, mealType) => {
    const dateKey = formatDateKey(date);
    return menuData[dateKey]?.[mealType] || [];
  };

  const handleGenerateAIMenu = async () => {
    if (!isAdmin) {
      toast.error('No tienes permisos para esta acción');
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('session_token');
      const startDate = formatDateKey(getWeekDays()[0]);
      const endDate = formatDateKey(getWeekDays()[6]);

      const response = await fetch(`${API}/api/ai-agents/generate-menu`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          preferences: {
            variety: true,
            balanced: true,
            seasonal: true
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Merge AI suggestions with current menu
        setMenuData(prev => ({
          ...prev,
          ...data.menu
        }));
        toast.success('Menú generado con IA');
      } else {
        toast.error('Error generando menú');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error de conexión');
    } finally {
      setGenerating(false);
    }
  };

  const handleAddProduct = (date, mealType, product) => {
    const dateKey = formatDateKey(date);
    setMenuData(prev => ({
      ...prev,
      [dateKey]: {
        ...(prev[dateKey] || {}),
        [mealType]: [
          ...(prev[dateKey]?.[mealType] || []),
          {
            product_id: product.product_id,
            name: product.name,
            price: product.price,
            image_url: product.image_url
          }
        ]
      }
    }));
    setShowProductPicker(false);
    toast.success(`${product.name} agregado`);
  };

  const handleRemoveProduct = (date, mealType, productId) => {
    const dateKey = formatDateKey(date);
    setMenuData(prev => ({
      ...prev,
      [dateKey]: {
        ...(prev[dateKey] || {}),
        [mealType]: (prev[dateKey]?.[mealType] || []).filter(p => p.product_id !== productId)
      }
    }));
    toast.success('Producto eliminado');
  };

  const handleSaveMenu = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/api/menu-calendar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ menu: menuData })
      });

      if (response.ok) {
        toast.success('Menú guardado exitosamente');
      } else {
        toast.error('Error guardando menú');
      }
    } catch (err) {
      toast.error('Error de conexión');
    }
  };

  const weekDays = getWeekDays();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-stone-800 flex items-center gap-3">
              <CalendarIcon className="w-8 h-8 text-emerald-600" />
              Calendario de Menú
            </h1>
            <p className="text-stone-500 mt-1">
              {isAdmin ? 'Programa el menú semanal' : 'Explora el menú de la semana'}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {isAdmin && (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleGenerateAIMenu}
                  disabled={generating}
                  className="gap-2"
                >
                  <Sparkles className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
                  {generating ? 'Generando...' : 'Generar con IA'}
                </Button>
                <Button 
                  onClick={handleSaveMenu}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                >
                  <Save className="w-4 h-4" />
                  Guardar Menú
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Week Navigation */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigateWeek(-1)}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-stone-600" />
            </button>
            
            <div className="text-center">
              <h2 className="text-lg font-semibold text-stone-800">
                {weekDays[0].toLocaleDateString('es-MX', { month: 'long', day: 'numeric' })} - {weekDays[6].toLocaleDateString('es-MX', { month: 'long', day: 'numeric', year: 'numeric' })}
              </h2>
            </div>
            
            <button 
              onClick={() => navigateWeek(1)}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-stone-600" />
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
            {/* Days Header */}
            <div className="grid grid-cols-7 border-b border-stone-200">
              {weekDays.map((date, i) => {
                const isToday = date.toDateString() === today.toDateString();
                const isPast = date < today;
                
                return (
                  <div 
                    key={i}
                    className={`p-4 text-center border-r border-stone-100 last:border-r-0 ${
                      isToday ? 'bg-emerald-50' : isPast ? 'bg-stone-50' : ''
                    }`}
                  >
                    <p className={`text-sm font-medium ${isPast ? 'text-stone-400' : 'text-stone-500'}`}>
                      {DAYS_SHORT[date.getDay()]}
                    </p>
                    <p className={`text-2xl font-bold mt-1 ${
                      isToday ? 'text-emerald-600' : isPast ? 'text-stone-400' : 'text-stone-800'
                    }`}>
                      {date.getDate()}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Meal Rows */}
            {MEAL_TYPES.map((meal) => (
              <div key={meal.id} className="border-b border-stone-100 last:border-b-0">
                {/* Meal Type Header */}
                <div className={`px-4 py-2 bg-${meal.color}-50 border-b border-${meal.color}-100`}>
                  <div className="flex items-center gap-2">
                    <meal.icon className={`w-4 h-4 text-${meal.color}-600`} />
                    <span className={`text-sm font-medium text-${meal.color}-700`}>{meal.name}</span>
                  </div>
                </div>
                
                {/* Products for each day */}
                <div className="grid grid-cols-7">
                  {weekDays.map((date, i) => {
                    const menuItems = getMenuForDay(date, meal.id);
                    const isPast = date < today;
                    
                    return (
                      <div 
                        key={i}
                        className={`min-h-[120px] p-2 border-r border-stone-100 last:border-r-0 ${
                          isPast ? 'bg-stone-50' : ''
                        }`}
                      >
                        <div className="space-y-1">
                          {menuItems.map((item, idx) => (
                            <div 
                              key={idx}
                              className="group relative bg-stone-100 rounded-lg p-2 text-xs hover:bg-stone-200 transition-colors"
                            >
                              <p className="font-medium text-stone-700 line-clamp-2">{item.name}</p>
                              <p className="text-emerald-600 font-semibold">${item.price}</p>
                              
                              {isAdmin && !isPast && (
                                <button
                                  onClick={() => handleRemoveProduct(date, meal.id, item.product_id)}
                                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          ))}
                          
                          {/* Add button */}
                          {isAdmin && !isPast && (
                            <button
                              onClick={() => {
                                setSelectedDay(date);
                                setSelectedMeal(meal.id);
                                setShowProductPicker(true);
                              }}
                              className="w-full p-2 border-2 border-dashed border-stone-300 rounded-lg text-stone-400 hover:border-emerald-400 hover:text-emerald-500 transition-colors flex items-center justify-center gap-1"
                            >
                              <Plus className="w-3 h-3" />
                              <span className="text-xs">Agregar</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-stone-500">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-50 rounded border border-emerald-200"></div>
            <span>Hoy</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-stone-50 rounded border border-stone-200"></div>
            <span>Días pasados</span>
          </div>
          {MEAL_TYPES.map(meal => (
            <div key={meal.id} className="flex items-center gap-2">
              <meal.icon className={`w-4 h-4 text-${meal.color}-600`} />
              <span>{meal.name}</span>
            </div>
          ))}
        </div>
      </main>

      {/* Product Picker Modal */}
      {showProductPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-stone-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-stone-800">Seleccionar Producto</h3>
                <p className="text-sm text-stone-500">
                  {selectedDay?.toLocaleDateString('es-MX', { weekday: 'long', month: 'long', day: 'numeric' })} - {MEAL_TYPES.find(m => m.id === selectedMeal)?.name}
                </p>
              </div>
              <button 
                onClick={() => setShowProductPicker(false)}
                className="p-2 hover:bg-stone-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {products.map(product => (
                  <button
                    key={product.product_id}
                    onClick={() => handleAddProduct(selectedDay, selectedMeal, product)}
                    className="bg-stone-50 hover:bg-emerald-50 rounded-xl p-3 text-left transition-colors border border-stone-200 hover:border-emerald-300"
                  >
                    <div className="aspect-video rounded-lg overflow-hidden bg-stone-200 mb-2">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl">🍽️</div>
                      )}
                    </div>
                    <p className="font-medium text-stone-800 text-sm line-clamp-2">{product.name}</p>
                    <p className="text-emerald-600 font-semibold">${product.price}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuCalendar;
